import { Platform } from "react-native";
import { router } from "expo-router";
import { getItem, setItem, removeItem } from "./storage";
import type { AuthResponse, Dog, Run, DogStats, Event, BreedRanking } from "./types";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";
const IS_WEB = Platform.OS === "web";
// Always sent on every request — backend uses this for cookie auth (web) and signup_source tracking
const PLATFORM_HEADER = Platform.OS === "ios" ? "ios" : Platform.OS === "android" ? "android" : "web";

// Concurrent-safe token refresh: all callers that hit 401 at the same time
// await the SAME in-flight refresh promise instead of one winning and the rest
// bailing out (which would force-signout the user even though the refresh was succeeding).
let refreshPromise: Promise<boolean> | null = null;

// Guards against multiple concurrent navigation triggers when several API
// calls all hit 401 at the same time.
let signOutInFlight = false;

// AuthContext registers a callback here so forceSignOut can clear React state
// (just clearing storage isn't enough — useProtectedRoute watches user state).
let signOutCallback: (() => void) | null = null;
export function registerSignOutHandler(cb: () => void) {
  signOutCallback = cb;
}

// Called when auth is completely dead — clears tokens and forces login screen.
// On web we reload the page; on native we clear React user state (which
// useProtectedRoute reacts to) AND replace the stack as a belt-and-suspenders.
async function forceSignOut() {
  if (signOutInFlight) return;
  signOutInFlight = true;
  try {
    await removeItem("accessToken");
    await removeItem("refreshToken");
    await removeItem("user");
    if (IS_WEB && typeof window !== "undefined") {
      window.location.href = "/";
    } else {
      // Clear React user state — useProtectedRoute in _layout.tsx will redirect
      try { signOutCallback?.(); } catch {}
      try {
        router.replace("/(auth)" as any);
      } catch {
        // Router may not be ready in edge cases (e.g. during initial mount) —
        // signOutCallback already cleared user state, app will redirect on next render.
      }
    }
  } finally {
    // Allow another sign-out attempt after a short delay so transient failures
    // can retry, but the navigation only fires once per burst.
    setTimeout(() => { signOutInFlight = false; }, 2000);
  }
}

async function getToken(): Promise<string | null> {
  if (IS_WEB) return null; // Web uses cookies, not tokens
  return getItem("accessToken");
}

async function doRefresh(): Promise<boolean> {
  try {
    const userStr = await getItem("user");
    if (!userStr) return false;
    const user = JSON.parse(userStr);

    if (IS_WEB) {
      // Web: cookie-based refresh — just send userId, cookie has the refresh token
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Platform": PLATFORM_HEADER },
        credentials: "include",
        body: JSON.stringify({ userId: user.id }),
      });
      return res.ok;
    } else {
      // Native: token-based refresh
      const refreshToken = await getItem("refreshToken");
      if (!refreshToken) return false;

      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id, refreshToken }),
      });

      if (!res.ok) return false;

      const data = await res.json();
      await setItem("accessToken", data.accessToken);
      await setItem("refreshToken", data.refreshToken);
      return true;
    }
  } catch {
    return false;
  }
}

async function refreshAccessToken(): Promise<boolean> {
  // If a refresh is already in flight, every concurrent caller waits for it
  // and gets the same outcome — preventing the "first call refreshes, second
  // call bails and force-signs-out" race.
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function apiRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  return request<T>(path, options);
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const headers: Record<string, string> = {
    ...((options.headers as Record<string, string>) || {}),
  };
  if (options.body) headers["Content-Type"] = "application/json";
  headers["X-Platform"] = PLATFORM_HEADER;
  if (!IS_WEB && token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  let res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
  });

  // If 401, try refreshing the token and retry once. We attempt refresh even
  // when the access token is missing — the refresh token (or web cookie) may
  // still be valid, and if not, the refresh attempt will fail fast and we'll
  // force-signout cleanly instead of bubbling the raw 401 to the UI.
  if (res.status === 401) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      if (!IS_WEB) {
        const newToken = await getToken();
        if (newToken) headers["Authorization"] = `Bearer ${newToken}`;
      }
      res = await fetch(`${API_URL}${path}`, {
        ...options,
        headers,
        ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
      });
    } else {
      await forceSignOut();
      throw new Error("Session expired");
    }
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || `HTTP ${res.status}`);
  }

  const text = await res.text();
  if (!text) return {} as T;
  return JSON.parse(text);
}

// Auth
export async function register(email: string, password: string, name: string, state?: string, zipCode?: string) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  headers["X-Platform"] = PLATFORM_HEADER;

  const res = await fetch(`${API_URL}/auth/register`, {
    method: "POST",
    headers,
    body: JSON.stringify({ email, password, name, state, zipCode }),
    ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: "Registration failed" }));
    throw new Error(error.message);
  }

  return res.json() as Promise<AuthResponse>;
}

export async function login(email: string, password: string) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  headers["X-Platform"] = PLATFORM_HEADER;

  const res = await fetch(`${API_URL}/auth/login`, {
    method: "POST",
    headers,
    body: JSON.stringify({ email, password }),
    ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: "Login failed" }));
    throw new Error(error.message);
  }

  return res.json() as Promise<AuthResponse>;
}

export async function forgotPassword(email: string) {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  headers["X-Platform"] = PLATFORM_HEADER;

  const res = await fetch(`${API_URL}/auth/forgot-password`, {
    method: "POST",
    headers,
    body: JSON.stringify({ email }),
    ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: "Request failed" }));
    throw new Error(error.message || "Request failed");
  }

  return res.json();
}

export async function logout() {
  try {
    const headers: Record<string, string> = {};
    headers["X-Platform"] = PLATFORM_HEADER;
    if (!IS_WEB) {
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
    }
    await fetch(`${API_URL}/auth/logout`, {
      method: "POST",
      headers,
      ...(IS_WEB ? { credentials: "include" as RequestCredentials } : {}),
    });
  } catch {}
}

// Screenshot import
export async function parseScreenshot(image: string) {
  return request<{ runs: Array<{ date: string; timeSeconds: number; speed: number; points: number }> }>("/screenshot-import/parse", {
    method: "POST",
    body: JSON.stringify({ image }),
  });
}

// Events
export async function getEvents(params?: { state?: string; zip?: string; lat?: number; lng?: number; radius?: number; homeZip?: string }) {
  const qs = params ? "?" + new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => [k, String(v)])
  ).toString() : "";
  return request<Event[]>(`/events${qs}`);
}

// Dogs
export async function getDogs() { return request<Dog[]>("/dogs"); }
export async function getDog(id: string) { return request<Dog>(`/dogs/${id}`); }
export async function createDog(data: Partial<Dog>) {
  return request<Dog>("/dogs", { method: "POST", body: JSON.stringify(data) });
}
export async function updateDog(id: string, data: Partial<Dog>) {
  return request<Dog>(`/dogs/${id}`, { method: "PUT", body: JSON.stringify(data) });
}
export async function deleteDog(id: string) {
  return request(`/dogs/${id}`, { method: "DELETE" });
}

// Runs
export async function getRuns(dogId: string) { return request<Run[]>(`/dogs/${dogId}/runs`); }
export async function getDogStats(dogId: string) { return request<DogStats>(`/dogs/${dogId}/stats`); }
export async function createRun(dogId: string, data: { date: string; timeSeconds: number; runNumber?: number; notes?: string; location?: string; eventName?: string; lureOperator?: string }) {
  return request<Run>(`/dogs/${dogId}/runs`, { method: "POST", body: JSON.stringify(data) });
}
export async function updateRun(runId: string, data: { date?: string; timeSeconds?: number; runNumber?: number; notes?: string | null; location?: string | null; eventName?: string | null; lureOperator?: string | null }) {
  return request<Run>(`/runs/${runId}`, { method: "PUT", body: JSON.stringify(data) });
}
export interface EventSearchHit { club: string; city: string | null; state: string | null; lure_operator: string | null }
export async function searchEvents(q: string): Promise<EventSearchHit[]> {
  if (!q || q.trim().length < 2) return [];
  return request<EventSearchHit[]>(`/events/search?q=${encodeURIComponent(q.trim())}`);
}
export async function deleteRun(runId: string) {
  return request(`/runs/${runId}`, { method: "DELETE" });
}

// Profile
export async function getProfile() {
  return request<{ id: string; email: string; name: string; state: string | null; zipCode: string | null }>("/auth/profile");
}
export async function updateProfile(data: { name?: string; state?: string; zipCode?: string }) {
  return request<{ id: string; email: string; name: string; state: string | null; zipCode: string | null }>("/auth/profile", {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

// User preferences
export async function getNotificationPreferences() {
  return request<{ rankingChanges: boolean; eventAlerts: boolean }>("/auth/notification-preferences");
}
export async function updateNotificationPreferences(prefs: { rankingChanges?: boolean; eventAlerts?: boolean }) {
  return request<{ rankingChanges: boolean; eventAlerts: boolean }>("/auth/notification-preferences", {
    method: "PUT",
    body: JSON.stringify(prefs),
  });
}

// Rankings
export async function getRankings(breed?: string, year?: string, sizeClass?: string) {
  const qs = new URLSearchParams();
  if (breed) qs.set("breed", breed);
  if (year) qs.set("year", year);
  if (sizeClass) qs.set("sizeClass", sizeClass);
  const q = qs.toString();
  return request<BreedRanking[]>(`/rankings${q ? "?" + q : ""}`);
}

export async function getBreeds() { return request<string[]>("/rankings/breeds"); }

// Follow feature
export async function getFollowedDogs() { return request<Dog[]>("/dogs/followed"); }
export async function followDog(dogId: string) {
  return request<{ dogId: string; following: boolean }>(`/dogs/${dogId}/follow`, { method: "POST" });
}
export async function unfollowDog(dogId: string) {
  return request<{ dogId: string; following: boolean }>(`/dogs/${dogId}/follow`, { method: "DELETE" });
}
export async function getFollowStatus(dogId: string) {
  return request<{ dogId: string; following: boolean; followerCount: number; isOwner: boolean }>(`/dogs/${dogId}/follow-status`);
}
export interface FollowerEntry {
  userId: string;
  name: string;
  followedAt: string;
  dogs: Array<{ id: string; name: string; breed: string; slug: string | null; isPublic: boolean }>;
}
export async function getDogFollowers(dogId: string) {
  return request<FollowerEntry[]>(`/dogs/${dogId}/followers`);
}
export async function searchPublicDogs(q: string) {
  if (!q || q.trim().length < 2) return [] as Array<{ id: string; name: string; breed: string; registeredName: string | null; photo: string | null; publicSlug: string | null }>;
  return request<Array<{ id: string; name: string; breed: string; registeredName: string | null; photo: string | null; publicSlug: string | null }>>(`/dogs/search?q=${encodeURIComponent(q)}`);
}
